import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { readAuthoredItem } from "../../../api/content/authoring/processAuthoringApi";
import { reportUiError, toUiError } from "../../../errors/uiError";
import { useLoad } from "../../../shared/loading/useLoad";
import { newItemId } from "../../../shared/references/itemIds";
import SourceView from "../../../ui/content/source/SourceView";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import Field from "../../../ui/forms/Field";
import OptionLabel from "../../../ui/forms/OptionLabel";
import Section from "../../../ui/forms/Section";
import { FORM_COMPACT_CONTROL_CLASS, FORM_HELP_TEXT_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { itemsOfKind, type AuthoringOptions } from "../draft/authoringOptions";
import FieldProblems from "../draft/FieldProblems";
import type { DefinitionCheck } from "../draft/useDefinitionCheck";
import { promptDraftOf, type PromptDraft } from "./promptDraft";

interface PromptFieldsProps {
  title: string;
  hint: string;
  /** The definition field whose problems show here. */
  path: string;
  /** `null`: none, offered only where the prompt is `optional`. */
  draft: PromptDraft | null;
  optional: boolean;
  /** A new prompt's name until the user writes one. */
  defaultName: string;
  options: AuthoringOptions;
  check: DefinitionCheck;
  /** The next draft, and the prompt's text where it was chosen or written; `null` where the text is
   * unchanged or couldn't be read. */
  onChange: (draft: PromptDraft | null, text: string | null) => void;
}

/**
 * A prompt an agent step pins (AU-4): an existing prompt version, shown as stored, or a new
 * prompt written here and saved before the step; for an optional one, none. The task prompt and
 * the step's own agent instructions both use it (WD-1+3 D-2).
 */
export default function PromptFields({ title, hint, path, draft, optional, defaultName, options, check, onChange }: PromptFieldsProps) {
  const { t } = useTranslation();
  const prompts = itemsOfKind(options, "prompt");
  const reference = draft?.kind === "existing" ? draft.reference : "";
  const load = useCallback((signal: AbortSignal) => (reference === "" ? Promise.resolve(null) : readAuthoredItem(reference, signal)), [reference]);
  const { state } = useLoad(load);
  const storedPrompt = state.status === "loaded" && state.value?.content?.kind === "prompt" ? state.value.content.definition : null;
  const shownText = draft?.kind === "new" ? draft.text : storedPrompt?.text ?? null;

  const choose = (chosen: string) => {
    readAuthoredItem(chosen).then(
      (item) => { onChange(promptDraftOf(chosen), item.content?.kind === "prompt" ? item.content.definition.text : ""); },
      (caught: unknown) => {
        reportUiError(toUiError(caught));
        onChange(promptDraftOf(chosen), null);
      },
    );
  };

  return (
    <Section title={title} hint={hint}>
      <div className="flex flex-wrap gap-4" role="radiogroup" aria-label={title}>
        {optional ? (
          <OptionLabel>
            <input type="radio" checked={draft === null} onChange={() => { onChange(null, null); }} />
            {t("authoring.step.agent.noPrompt")}
          </OptionLabel>
        ) : null}
        <OptionLabel>
          <input
            type="radio"
            checked={draft?.kind === "existing"}
            disabled={prompts.length === 0}
            onChange={() => {
              const first = prompts[0];
              if (first !== undefined) {
                choose(first.reference);
              }
            }}
          />
          {t("authoring.step.agent.existingPrompt")}
        </OptionLabel>
        <OptionLabel>
          <input
            type="radio"
            checked={draft?.kind === "new"}
            onChange={() => {
              const text = shownText ?? "";
              onChange({ kind: "new", itemId: newItemId(), name: defaultName, description: storedPrompt?.description ?? "", text }, text);
            }}
          />
          {t("authoring.step.agent.newPrompt")}
        </OptionLabel>
      </div>
      {draft?.kind === "existing" ? (
        <>
          <select className={FORM_SELECT_CLASS} aria-label={`${title}: ${t("authoring.step.agent.existingPrompt")}`} value={draft.reference} onChange={(event) => { choose(event.target.value); }}>
            {prompts.some((candidate) => candidate.reference === draft.reference) ? null : <option value={draft.reference}>{draft.reference}</option>}
            {prompts.map((candidate) => <option key={candidate.reference} value={candidate.reference}>{candidate.name}</option>)}
          </select>
          <LoadStatus state={state} loadingLabel={t("authoring.step.agent.loadingPrompt")} compact />
          {shownText === null ? null : <SourceView label={t("authoring.step.agent.promptText")} language="markdown" value={shownText} />}
        </>
      ) : null}
      {draft?.kind === "new" ? (
        <>
          <Field label={t("authoring.step.agent.promptName")}>
            {(control) => (
              <input {...control} className={FORM_COMPACT_CONTROL_CLASS} value={draft.name} maxLength={200} onChange={(event) => { onChange({ ...draft, name: event.target.value }, null); }} />
            )}
          </Field>
          <Field label={t("authoring.step.agent.promptDescription")}>
            {(control) => (
              <input {...control} className={FORM_COMPACT_CONTROL_CLASS} value={draft.description} onChange={(event) => { onChange({ ...draft, description: event.target.value }, null); }} />
            )}
          </Field>
          <Field label={t("authoring.step.agent.promptText")}>
            {(control) => (
              <textarea
                {...control}
                className={`${FORM_COMPACT_CONTROL_CLASS} font-mono text-xs`}
                rows={10}
                value={draft.text}
                onChange={(event) => { onChange({ ...draft, text: event.target.value }, event.target.value); }}
              />
            )}
          </Field>
          <p className={FORM_HELP_TEXT_CLASS}>{t("authoring.step.agent.newPromptHint")}</p>
        </>
      ) : null}
      <FieldProblems check={check} path={path} />
    </Section>
  );
}
